/* No-login fixture for the ported CRM app (?e2e=crm). Wraps CrmApp in the
   DataContextTestProvider with a small CRM-shaped seed — including the fields
   the 2026-08-26 contact-ingest work introduced (clientId, crmOrigin,
   parentClientId) and the CRM v3 slice 1 fields (consent, tags, social,
   lastTouch, noModelSessions) — so the screens can be verified visually and by
   Playwright without Firebase. Local tab state stands in for the app topbar.

   Dates are computed FROM TODAY rather than written down, so "2 d ago" and
   "over a year" keep meaning the same thing next month and the e2e
   assertions never rot. */
import React, { useRef, useState } from "react";
import { DataContextTestProvider } from "../context/DataContext.jsx";
import { CrmAppInner as CrmApp } from "../apps/crm/CrmApp.jsx";
import { __setInteractionsCache } from "../apps/crm/useAllInteractions.js";
import { __setOpenItemsCache } from "../apps/crm/useOpenItems.js";
import { __setCampaignApi } from "../lib/campaignCallables.js";
import { C, font, btnS } from "../lib/styles.js";
import { localE2EMode } from "./e2eMode.js";

/* main.jsx imports this file on EVERY page load, nycsuite.io included, so the
   three module-scope stand-ins below (interactions cache, open-items cache,
   the campaignSend client) install only when this fixture is the page. */
const FIXTURE_ACTIVE = localE2EMode() === "crm";

const DAY = 86_400_000;
const daysAgo = (n) => new Date(Date.now() - n * DAY).toISOString().slice(0, 10);
const daysAhead = (n) => new Date(Date.now() + n * DAY).toISOString().slice(0, 10);
const touch = (days, channel) => ({ at: `${daysAgo(days)}T15:00:00Z`, channel, ixId: "", summary: "" });

const CONTACTS = [
  {
    id: "ct_gil", name: "Gil Sperling", emails: ["gils@frostproductions.com"],
    phone: "555-0100", company: "Frost Productions", clientId: "cl_frost",
    industry: ["museum_archival"], role: ["customer"], leadSource: "referral",
    score: 72, firstContact: "2025-07-29", lastContact: daysAgo(11),
    mailchimpStatus: "unsubscribed", linkedClients: [{ clientId: "cl_frost", linkType: "employee" }],
    crmOrigin: { role: "contact", source: "email_from", quoteId: "q_frost", at: "2026-08-26" },
    notes: "Video services manager. Referred by Todd Bryant.",
    tags: ["monument"],
    consent: { state: "relationship", source: "quote", at: "2026-08-26", unsubscribed: false },
    lastTouch: touch(11, "mail_out"),
    social: { linkedin: { url: "https://linkedin.com/in/gilsperling", connected: false } },
  },
  {
    id: "ct_hazel", name: "Hazel Burke", emails: ["hazelb725@gmail.com"],
    phone: "", company: "Red Barn Film Productions DAC", clientId: "cl_redbarn",
    industry: ["film_tv"], role: ["prospect"], leadSource: "referral",
    score: 50, firstContact: "2026-08-24", lastContact: daysAgo(13),
    mailchimpStatus: "unsubscribed", linkedClients: [{ clientId: "cl_redbarn", linkType: "employee" }],
    crmOrigin: { role: "contact", source: "email_from", quoteId: "q_red", at: "2026-08-24" },
    notes: "Recovered by contact backfill 2026-08-26.",
    consent: { state: "no_bulk", source: "migration", at: "2026-09-06", unsubscribed: false },
    lastTouch: touch(13, "mail_in"),
  },
  {
    id: "ct_carl", name: "Carl Hampe", emails: ["carl.hampe@icloud.com"],
    phone: "", company: "Red Barn Film Productions DAC", clientId: "cl_redbarn",
    industry: ["film_tv"], role: ["prospect"], leadSource: "",
    score: 50, firstContact: "2026-08-24", lastContact: daysAgo(13),
    mailchimpStatus: "unsubscribed", linkedClients: [{ clientId: "cl_redbarn", linkType: "employee" }],
    crmOrigin: { role: "cc", source: "email_cc", quoteId: "q_red", at: "2026-08-24" },
    consent: { state: "no_bulk", source: "migration", at: "2026-09-06", unsubscribed: false },
  },
  /* Seeded for the ⌘K palette spec (tests/e2e/crm-search.spec.js): a phone
     number searchable by its last four digits, and an accented name.
     Ben is also the mockup's referrer row — his money is all referred. */
  {
    id: "ct_ben", name: "Ben Nunez", emails: ["ben@evercoast.com"],
    phone: "+1 917 555 0142", company: "Evercoast", clientId: "cl_ever",
    jobTitle: "Partnerships",
    industry: ["film_tv", "spatial"], role: ["referrer", "ally"], leadSource: "conference",
    score: 60, firstContact: "2025-03-01", lastContact: daysAgo(207),
    notes: "Partnerships. Referred the monument job at HISART.",
    tags: ["volumetric"],
    consent: { state: "relationship", source: "job", at: "2026-02-11", unsubscribed: false },
    lastTouch: touch(207, "mail_in"),
    social: { linkedin: { url: "https://linkedin.com/in/bennunez", connected: true, connectedOn: "Mar 2023" } },
    linkedClients: [{ clientId: "cl_ever", linkType: "employee" }],
  },
  {
    id: "ct_amelie", name: "Amélie Fauré", emails: ["amelie@faure.fr"],
    phone: "", company: "Faure Studio", industry: ["museum_archival"],
    role: ["prospect"], score: 40, firstContact: "2026-01-02", lastContact: "2026-01-02",
    lastTouch: touch(247, "mail_out"),
  },
  /* Quoted, never booked: the "$0 quoted $23,850" cell. */
  {
    id: "ct_dave", name: "Dave Sales", emails: ["dave@ninetwelve.tv"],
    phone: "", company: "Nine Twelve Productions", clientId: "cl_912",
    linkedClients: [{ clientId: "cl_912", linkType: "employee" }],
    industry: ["film_tv"], role: ["qualified"], leadSource: "website",
    tags: ["lidar", "drone"],
    consent: { state: "relationship", source: "quote", at: "2026-09-03", unsubscribed: false },
    lastTouch: touch(3, "quote"),
  },
  /* Opted in, direct revenue, connected on LinkedIn. */
  {
    id: "ct_matt", name: "Matt Knudsen", emails: ["matt@artcamp.co"],
    phone: "+1 212 555 0180", company: "Art Camp", clientId: "cl_artcamp",
    linkedClients: [{ clientId: "cl_artcamp", linkType: "employee" }],
    industry: ["commercial_product"], role: ["customer"], leadSource: "referral",
    tags: ["sneakers"],
    consent: { state: "opted_in", source: "website", at: "2026-04-02", unsubscribed: false },
    lastTouch: touch(98, "job"),
    social: { linkedin: { url: "https://linkedin.com/in/mattknudsen", connected: true, connectedOn: "Jan 2024" } },
  },
  {
    id: "ct_mollie", name: "Mollie Chen", emails: ["mollie.chen@rocnation.com"],
    phone: "", company: "Roc Nation", clientId: "cl_rocnat",
    linkedClients: [{ clientId: "cl_rocnat", linkType: "employee" }],
    industry: ["music"], role: ["customer"],
    consent: { state: "relationship", source: "job", at: "2026-08-31", unsubscribed: false },
    lastTouch: touch(6, "mail_in"),
  },
  {
    id: "ct_pat", name: "Patrick Whitaker", emails: ["patrick@whimal.com"],
    phone: "", company: "Roc Nation", clientId: "cl_rocnat",
    linkedClients: [{ clientId: "cl_rocnat", linkType: "employee" }],
    industry: ["music"], role: ["collaborator"],
    consent: { state: "no_bulk", source: "migration", at: "2026-09-06", unsubscribed: false },
    lastTouch: touch(6, "mail_in"),
    social: { linkedin: { url: "https://linkedin.com/in/pwhitaker", connected: true, connectedOn: "Jun 2022" } },
  },
  /* Unsubscribed, and on an NDA client: the two flags the Consent cell has to
     show together. */
  {
    id: "ct_randy", name: "Randy Starr", emails: ["randy.starr@apple.studio"],
    phone: "", company: "Apple", clientId: "cl_apple",
    linkedClients: [{ clientId: "cl_apple", linkType: "employee" }],
    industry: ["film_tv"], role: ["customer"], tags: ["nda"],
    consent: { state: "no_bulk", source: "manual", at: "2026-09-05", unsubscribed: true },
    lastTouch: touch(1, "mail_in"),
  },
  {
    id: "ct_peter", name: "Peter Ruiz", emails: ["peter@dronecrew.nyc"],
    phone: "", company: "Part 107 pilot", industry: ["film_tv"], role: ["vendor"],
    tags: ["drone"],
    consent: { state: "no_bulk", source: "migration", at: "2026-09-06", unsubscribed: false },
    lastTouch: touch(4, "mail_out"),
  },
];

const CLIENTS = [
  {
    id: "cl_frost", name: "Frost Productions", clientCode: "fropro",
    industry: ["museum_archival"], email: "", orgDomain: "frostproductions.com",
    status: "active", firstContact: "2025-07-29", lastContact: daysAgo(11),
    linkedContacts: [{ contactId: "ct_gil", role: "contact" }],
    lastTouch: touch(11, "mail_out"),
  },
  {
    id: "cl_redbarn", name: "Red Barn Film Productions DAC", clientCode: "redbar",
    industry: ["film_tv"], email: "", orgDomain: "",
    status: "active", firstContact: "2026-08-24", lastContact: daysAgo(13),
    linkedContacts: [
      { contactId: "ct_hazel", role: "contact" },
      { contactId: "ct_carl", role: "contact" },
    ],
  },
  {
    id: "cl_912", name: "Nine Twelve Productions, LLC", clientCode: "ninetw",
    industry: ["film_tv"], orgDomain: "ninetwelve.tv", status: "active",
    linkedContacts: [{ contactId: "ct_dave", role: "contact" }],
  },
  {
    id: "cl_artcamp", name: "Art Camp", clientCode: "artcam",
    industry: ["commercial_product"], orgDomain: "artcamp.co", status: "active",
    linkedContacts: [{ contactId: "ct_matt", role: "contact" }],
    tags: ["sneakers"],
  },
  {
    id: "cl_rocnat", name: "Roc Nation", clientCode: "rocnat",
    industry: ["music"], orgDomain: "rocnation.com", status: "active",
    linkedContacts: [
      { contactId: "ct_mollie", role: "billed_client" },
      { contactId: "ct_pat", role: "contact" },
    ],
  },
  {
    id: "cl_apple", name: "Apple", clientCode: "apple",
    industry: ["film_tv"], orgDomain: "apple.studio", status: "active",
    noModelSessions: true,
    linkedContacts: [{ contactId: "ct_randy", role: "contact" }],
  },
  {
    id: "cl_ever", name: "Evercoast", clientCode: "everco",
    industry: ["spatial"], orgDomain: "evercoast.com", status: "active",
    linkedContacts: [{ contactId: "ct_ben", role: "contact" }],
  },
  /* Ben sent us this one — the referral bucket on his row. */
  {
    id: "cl_hisart", name: "HISART", clientCode: "hisart",
    industry: ["museum_archival"], orgDomain: "hisart.org", status: "inactive",
    referredBy: "ct_ben",
  },
];

const JOBS = [
  {
    id: "j_frost1", jobId: "fropro_260830", clientId: "cl_frost",
    clientName: "Frost Productions", contactName: "Gil Sperling",
    status: "scheduled", captureDate: daysAgo(7), grossRevenue: 4800,
    linkedContacts: [{ contactId: "ct_gil", role: "contact" }],
  },
  /* A parent project and its change order: the Jobs count says 1 and the
     money says $11,000, because a change order is part of its parent. */
  {
    id: "j_artcamp", jobId: "artcam_260529", clientId: "cl_artcamp",
    clientName: "Art Camp", status: "completed", captureDate: daysAgo(98),
    grossRevenue: 9900, linkedContacts: [{ contactId: "ct_matt", role: "contact" }],
  },
  {
    id: "j_artcamp_co", jobId: "artcam_260605", changeOrderOf: "j_artcamp",
    clientId: "cl_artcamp", clientName: "Art Camp", status: "completed",
    captureDate: daysAgo(91), grossRevenue: 1100,
    linkedContacts: [{ contactId: "ct_matt", role: "contact" }],
  },
  /* Out of the twelve-month window: lifetime only. */
  {
    id: "j_artcamp_old", jobId: "artcam_250101", clientId: "cl_artcamp",
    clientName: "Art Camp", status: "completed", captureDate: daysAgo(500),
    grossRevenue: 20000,
  },
  {
    id: "j_rocnat", jobId: "rocnat_260831", clientId: "cl_rocnat",
    clientName: "Roc Nation", status: "completed", captureDate: daysAgo(6),
    grossRevenue: 6800, linkedContacts: [{ contactId: "ct_mollie", role: "billed_client" }],
  },
  {
    id: "j_apple", jobId: "apple_260911", clientId: "cl_apple",
    clientName: "Apple", status: "booked", captureDate: daysAgo(1),
    grossRevenue: 7038, linkedContacts: [{ contactId: "ct_randy", role: "contact" }],
  },
  {
    id: "j_hisart", jobId: "hisart_250910", clientId: "cl_hisart",
    clientName: "HISART", status: "completed", captureDate: daysAgo(360),
    grossRevenue: 41200,
  },
];

const QUOTES = [
  {
    id: "q_frost", filename: "nyc_fropro_260826", clientId: "cl_frost",
    clientName: "Frost Productions", contactName: "Gil Sperling",
    quoteDate: daysAgo(11), grandTotal: 4800, jobStatus: "booked",
    lineItems: [{ description: "Monument capture, half day" }],
  },
  /* Open, never booked — the "$0 quoted $23,850" cell on both tabs. */
  {
    id: "q_912", filename: "nyc_ninetw_260903", clientId: "cl_912",
    clientName: "Nine Twelve Productions, LLC", contactName: "Dave Sales",
    quoteDate: daysAgo(3), grandTotal: 23850, jobStatus: "quoted",
    lineItems: [{ description: "Four October capture days" }],
  },
];

/* Holds are keyed by quote id (holds slice 2 contract). The Nine Twelve dates
   are on the calendar but not yet a job, which is the "held" suffix. */
const HOLDS = [
  {
    id: "q_912", label: "Nine Twelve", clientName: "Nine Twelve Productions, LLC",
    subject: "October capture", dates: [{ date: daysAhead(29) }, { date: daysAhead(30) }],
    equipmentIds: [], crew: [], fingerprint: "", updatedAt: new Date().toISOString(),
  },
];

/* The palette's Messages group reads the whole `interactions` collection
   through useAllInteractions, which talks to Firestore. The fixture has no
   Firebase, so it seeds that hook's session cache directly — same records the
   admin-only read would have returned. */
const INTERACTIONS = [
  {
    id: "in_budget", type: "message", subject: "Re: Budget range for the figure group",
    from: "ben@evercoast.com", to: ["ivin@newyorkcapture.com"], at: "2026-09-04T15:00:00Z",
    contactIds: ["ct_ben"],
    body: "A 7 ft monument not yet sculpted, and the number to reach me on is 917 555 0142.",
  },
  {
    id: "in_springs", type: "message", subject: "Re: Springs estate capture",
    from: "gils@frostproductions.com", at: "2026-06-16T09:00:00Z", contactIds: ["ct_gil"],
    body: "The monument stone near the barn is the one they want scanned first.",
  },
  /* Three /crm hello check-ins (slice 5's running row). Ben answered inside
     the fortnight; Peter did not. The third is last month, so it counts
     towards "all time" and not towards "this month". */
  {
    id: "in_hello_ben", type: "message", direction: "out", outreach: "hello",
    at: `${daysAgo(2)}T14:00:00Z`, contactIds: ["ct_ben"],
    subject: "Checking in", summary: "Checked in with Ben, no pitch",
  },
  {
    id: "in_hello_peter", type: "message", direction: "out", outreach: "hello",
    at: `${daysAgo(4)}T14:00:00Z`, contactIds: ["ct_peter"],
    subject: "Checking in", summary: "Checked in with Peter about October",
  },
  {
    id: "in_hello_amelie", type: "message", direction: "out", outreach: "hello",
    at: `${daysAgo(40)}T14:00:00Z`, contactIds: ["ct_amelie"],
    subject: "Checking in", summary: "Checked in with Amelie",
  },
  {
    id: "in_hello_ben_reply", type: "message", direction: "in",
    at: `${daysAgo(1)}T14:00:00Z`, contactIds: ["ct_ben"],
    summary: "Good to hear from you, the monument is still moving",
  },
  /* An inbound reply to the sent campaign below: Matt is on its frozen list. */
  {
    id: "in_reply_matt", type: "message", direction: "in",
    at: `${daysAgo(3)}T09:00:00Z`, contactIds: ["ct_matt"],
    summary: "we have 40 pairs in October",
  },
];
if (FIXTURE_ACTIVE) __setInteractionsCache(INTERACTIONS);

/* ── Segments and campaigns (CRM v3 slices 4 and 5) ──────────────────────────
   Slice 4 owns the Segments tab; this fixture carries one segment only so the
   Campaigns tab has something to resolve. Film & TV here is Hazel, Carl, Ben,
   Dave, Randy and Peter — six members, of whom Ben and Dave are the two the
   consent rule lets bulk mail reach. */
const SEGMENTS = [
  {
    id: "sg_filmtv", name: "Film & TV · quoted 2026, not booked",
    rules: [{ field: "industry", op: "any_of", value: ["film_tv"] }],
    source: "chat", note: "Written by /crm campaign.",
    createdBy: "ivin@newyorkcapture.com", createdAt: `${daysAgo(1)}T14:00:00Z`,
  },
  {
    id: "sg_film", name: "Film & TV · never booked",
    rules: [
      { field: "industry", op: "any_of", value: ["film_tv"] },
      { field: "job", op: "none" },
      { field: "consent", op: "is", value: ["opted_in", "relationship"] },
    ],
    source: "chat", createdBy: "ivin@newyorkcapture.com",
    createdAt: `${daysAgo(0)}T09:00:00Z`, updatedAt: `${daysAgo(0)}T09:00:00Z`,
    note: "The audience the LiDAR campaign draft is built on.",
  },
  {
    id: "sg_quiet", name: "Went quiet · 6 months+",
    rules: [
      { field: "last_touch", op: "over", value: 180 },
      { field: "consent", op: "is", value: ["opted_in", "relationship"] },
    ],
    source: "manual", createdBy: "ivin@newyorkcapture.com",
    createdAt: `${daysAgo(4)}T10:00:00Z`, updatedAt: `${daysAgo(4)}T10:00:00Z`,
  },
  /* Someone else's, and older than the "Used recently" window — the two chips
     that would otherwise have nothing to hide. */
  {
    id: "sg_sneakers", name: "Fashion & product · sneakers",
    rules: [
      { field: "tag", op: "any_of", value: ["sneakers"] },
      { field: "consent", op: "is", value: ["opted_in", "relationship"] },
    ],
    source: "manual", createdBy: "someone.else@newyorkcapture.com",
    createdAt: `${daysAgo(38)}T10:00:00Z`, updatedAt: `${daysAgo(38)}T10:00:00Z`,
  },
];

const blankCampaign = (over) => ({
  audience: { frozenAt: null, contactIds: [], counts: { total: 0, opted_in: 0, relationship: 0, no_bulk: 0, held_back: 0 } },
  brief: { goal: "", angle: "", exclusions: [], followUp: "", evidence: [] },
  copy: { subject: "", body: "", ctaText: "", ctaUrl: "", versions: [] },
  schedule: { at: null, timezone: "America/New_York" },
  send: { mailchimpCampaignId: "", sentAt: "", error: "" },
  results: { delivered: 0, opened: 0, clicked: 0, replied: 0, unsubscribed: 0, bounced: 0, syncedAt: "" },
  origin: { source: "manual", at: "", by: "ivin@newyorkcapture.com", writeId: "" },
  createdBy: "ivin@newyorkcapture.com", deletedAt: null,
  ...over,
});

const CAMPAIGNS = [
  /* Awaiting approval, written by the chat. This is the drawer the mockup
     draws, and the one the approval-bar assertions run against. */
  blankCampaign({
    id: "cp_lidar", name: "LiDAR for productions", channel: "email", status: "review",
    segmentId: "sg_filmtv",
    brief: {
      goal: "Reopen film and TV quotes that never booked, with something to read, not a pitch",
      angle: "38 of 61 asked about LiDAR or environments; none have seen the Gilder Center numbers",
      exclusions: [{ rule: "open quote under 30 days", count: 4 }, { rule: "emailed one-to-one this week", count: 0 }],
      followUp: "Replies land in the inbox; the sweep flags them the next morning",
      evidence: [
        { kind: "quote", id: "q_912", label: "12 quotes, 2026, film and TV" },
        { kind: "post", id: "gilder-center-scan", label: "Blog post gilder-center-scan" },
      ],
    },
    copy: {
      subject: "What a LiDAR day looks like on a working set",
      body: "Hi {{first name}},\n\nLast month we scanned the Gilder Center atrium at the Natural History Museum: 35,000 square feet, 72 setups, one day, three of us.\n\nThanks,\nIvin",
      ctaText: "The Gilder Center numbers", ctaUrl: "https://newyorkcapture.com/blog/gilder-center-scan",
      versions: [],
    },
    origin: { source: "chat", at: `${daysAgo(0)}T14:12:00Z`, by: "ivin@newyorkcapture.com", writeId: "w_1" },
    createdAt: `${daysAgo(0)}T14:12:00Z`,
  }),
  /* Sent, frozen, with results and one reply from the inbox. */
  blankCampaign({
    id: "cp_fall", name: "Fall 2026 studio update", channel: "email", status: "sent",
    segmentId: "sg_filmtv",
    audience: {
      frozenAt: `${daysAgo(5)}T13:00:00Z`,
      contactIds: ["ct_matt", "ct_mollie", "ct_gil"],
      counts: { total: 3, opted_in: 1, relationship: 2, no_bulk: 0, held_back: 0 },
    },
    brief: { goal: "Tell the people who have gone quiet that we are back at the Navy Yard", angle: "", exclusions: [], followUp: "", evidence: [] },
    copy: { subject: "Back at the Navy Yard, and what changed", body: "Hi,\n\nThanks,\nIvin", ctaText: "", ctaUrl: "", versions: [] },
    schedule: { at: `${daysAgo(4)}T13:40:00Z`, timezone: "America/New_York" },
    send: { mailchimpCampaignId: "mc_fall", sentAt: `${daysAgo(4)}T13:40:00Z`, error: "" },
    results: { delivered: 3, opened: 2, clicked: 1, replied: 0, unsubscribed: 1, bounced: 0, syncedAt: `${daysAgo(0)}T11:00:00Z` },
    origin: { source: "manual", at: `${daysAgo(6)}T13:00:00Z`, by: "ivin@newyorkcapture.com", writeId: "" },
    createdAt: `${daysAgo(6)}T13:00:00Z`,
  }),
  /* Approved and frozen, not yet handed to Mailchimp: the state an edit to
     the copy has to thaw. */
  blankCampaign({
    id: "cp_museums", name: "Museums, the Gilder Center numbers", channel: "email", status: "approved",
    segmentId: "sg_filmtv",
    audience: {
      frozenAt: `${daysAgo(1)}T13:00:00Z`,
      contactIds: ["ct_ben", "ct_dave"],
      counts: { total: 6, opted_in: 0, relationship: 2, no_bulk: 4, held_back: 0 },
    },
    brief: { goal: "Show the museums what 35,000 square feet in a day looks like", angle: "", exclusions: [], followUp: "", evidence: [] },
    copy: { subject: "How we scanned 35,000 square feet in a day", body: "Hi,\n\nThanks,\nIvin", ctaText: "", ctaUrl: "", versions: [] },
    schedule: { at: `${daysAhead(2)}T13:40:00Z`, timezone: "America/New_York" },
    origin: { source: "manual", at: `${daysAgo(2)}T13:00:00Z`, by: "ivin@newyorkcapture.com", writeId: "" },
    createdAt: `${daysAgo(2)}T13:00:00Z`,
  }),
  /* Approved, and its schedule call answers with a rejection AFTER the server
     has already scheduled it. The drawer has to reload and believe the record.  */
  blankCampaign({
    id: "cp_ghost", name: "Winter studio note", channel: "email", status: "approved",
    segmentId: "sg_filmtv",
    audience: {
      frozenAt: `${daysAgo(1)}T13:00:00Z`,
      contactIds: ["ct_ben", "ct_dave"],
      counts: { total: 6, opted_in: 0, relationship: 2, no_bulk: 4, held_back: 0 },
    },
    brief: { goal: "Tell them what changed over the winter", angle: "", exclusions: [], followUp: "", evidence: [] },
    copy: { subject: "What changed over the winter", body: "Hi,\n\nThanks,\nIvin", ctaText: "", ctaUrl: "", versions: [] },
    schedule: { at: `${daysAhead(3)}T13:40:00Z`, timezone: "America/New_York" },
    origin: { source: "manual", at: `${daysAgo(2)}T13:00:00Z`, by: "ivin@newyorkcapture.com", writeId: "" },
    createdAt: `${daysAgo(2)}T13:00:00Z`,
  }),
  /* A one-to-one sequence: "rolling", no Mailchimp anywhere near it. */
  blankCampaign({
    id: "cp_thanks", name: "Referrer thank-yous", channel: "one_to_one", status: "scheduled",
    segmentId: "sg_filmtv",
    audience: { frozenAt: `${daysAgo(8)}T13:00:00Z`, contactIds: ["ct_ben"], counts: { total: 1, opted_in: 0, relationship: 1, no_bulk: 0, held_back: 0 } },
    brief: { goal: "Thank the people who sent work our way this year", angle: "", exclusions: [], followUp: "", evidence: [] },
    copy: { subject: "", body: "", ctaText: "", ctaUrl: "", versions: [] },
    origin: { source: "chat", at: `${daysAgo(8)}T13:00:00Z`, by: "ivin@newyorkcapture.com", writeId: "w_2" },
    createdAt: `${daysAgo(8)}T13:00:00Z`,
  }),
  /* A draft with no copy: it must NOT be promoted to review just by opening. */
  blankCampaign({
    id: "cp_sneaker", name: "Sneaker scanning, flat $900", channel: "email", status: "draft",
    segmentId: "",
    brief: { goal: "Tell the product people the flat rate", angle: "", exclusions: [], followUp: "", evidence: [] },
    origin: { source: "manual", at: `${daysAgo(17)}T13:00:00Z`, by: "ivin@newyorkcapture.com", writeId: "" },
    createdAt: `${daysAgo(17)}T13:00:00Z`,
  }),
  /* The retired nyc-suite wizard's shape, still in the same collection. The
     table must not draw it. */
  { id: "camp_1757030400000", subject: "Summer 2025 update", status: "sent", recipientIds: [] },
];

/* The Mailchimp callable, stubbed. The fixture has no Firebase and, more to
   the point, a test must never be one network hop from a real send. */
/* The stub stands in for the callable, which in production writes the campaign
   record on the SERVER before it answers. `serverWrite` is that write: the
   fixture's own state is the stand-in for Firestore, and `reloadCampaigns`
   below is what reads it back. Held in a box because the stub is installed at
   module scope, before the component exists. */
const server = { write: null, read: null };

if (FIXTURE_ACTIVE) __setCampaignApi({
  test: async ({ campaignId, to }) => {
    server.write?.(campaignId, (c) => ({ ...c, send: { ...(c.send || {}), mailchimpCampaignId: "mc_stub" } }));
    return { mailchimpCampaignId: "mc_stub", sentTo: to };
  },
  schedule: async ({ campaignId }) => {
    const scheduledFor = `${daysAhead(1)}T13:45:00Z`;
    /* Every schedule writes the record server-side first, exactly as the
       callable does — the identity write lands before anything irreversible. */
    server.write?.(campaignId, (c) => ({
      ...c,
      status: "scheduled",
      schedule: { ...(c.schedule || {}), at: scheduledFor },
      send: {
        ...(c.send || {}),
        mailchimpCampaignId: `mc_${campaignId}`,
        mailchimpSegmentId: "4021",
        recipientIds: (c.audience?.contactIds || []).slice(0, 2),
        error: "",
      },
    }));
    /* cp_ghost is the lost response: the server did the work and the answer
       never arrived. The drawer must show the truth, not write its own stale
       snapshot back over it. */
    if (campaignId === "cp_ghost") throw new Error("deadline-exceeded");
    return { status: "scheduled", mailchimpCampaignId: `mc_${campaignId}`, scheduledFor, sentAt: "", recipients: 2, dropped: 0 };
  },
  sync: async () => ({ status: "measured", results: {}, touches: 0 }),
});

/* The Clients list's Open column: one query for type == "open_item" AND
   status == "open". The fixture seeds exactly what that query would return. */
const OPEN_ITEMS = [
  {
    id: "in_permit", type: "open_item", status: "open", clientId: "cl_912",
    owner: "client", date: daysAgo(3), summary: "Permit due Sept 7",
  },
  {
    id: "in_invoice", type: "open_item", status: "open", clientId: "cl_rocnat",
    owner: "nyc", date: daysAgo(4), summary: "Invoice not raised",
  },
];
if (FIXTURE_ACTIVE) __setOpenItemsCache(OPEN_ITEMS);

/* Three segments for the Segments tab (slice 4). Rules only — every count the
   tab shows is resolved live from the collections above, so these records
   carry no membership and cannot go stale. */

/* `?e2e=crm&seed=3400` pads the book out to N contacts with synthetic rows, so
   the list can be timed at the size the real CRM actually is (3,459 contacts
   on 2026-09-06). The padding carries the fields the columns read — consent, a
   last touch, tags, an employer — so nothing is measured on empty records.
   Off unless the parameter is present. */
const SEED_CAP = 20_000;

/** How long the fixture's segment save takes, from `?slowsave=<ms>`. Capped,
 *  and any nonsense reads as 0 — the same rule the seed parameter follows. */
const SLOW_SAVE_MS = (() => {
  const raw = Number(new URLSearchParams(window.location.search).get("slowsave"));
  return Number.isSafeInteger(raw) && raw > 0 ? Math.min(raw, 5_000) : 0;
})();

function seededContacts() {
  /* Anything that is not a safe whole number is no seed at all, and the cap is
     there because ?seed=1e9 is one keystroke away from wedging the browser on
     a fixture URL. The real CRM is 3,459 contacts; 20,000 is already well past
     anything worth timing. */
  const raw = Number(new URLSearchParams(window.location.search).get("seed"));
  const want = Number.isSafeInteger(raw) && raw > 0 ? Math.min(raw, SEED_CAP) : 0;
  if (!want || want <= CONTACTS.length) return CONTACTS;
  const states = ["opted_in", "relationship", "no_bulk"];
  const channels = ["mail_in", "mail_out", "quote", "job", "campaign", "note"];
  const employers = CLIENTS.map((c) => c.id);
  const extra = Array.from({ length: want - CONTACTS.length }, (_, i) => ({
    id: `ct_seed_${i}`,
    name: `Seed Contact ${i}`,
    emails: [`seed${i}@example.com`, `seed${i}.alt@example.com`],
    phone: `+1 212 555 ${String(i % 10000).padStart(4, "0")}`,
    company: `Seed Company ${i % 400}`,
    clientId: i % 5 === 0 ? employers[i % employers.length] : "",
    industry: [Object.keys(INDUSTRY_KEYS)[i % 15]],
    role: [["customer", "prospect", "qualified", "referrer", "vendor"][i % 5]],
    tags: [`tag${i % 40}`, `tag${(i + 7) % 40}`],
    consent: { state: states[i % 3], source: "migration", at: "2026-09-06", unsubscribed: i % 97 === 0 },
    lastTouch: touch(i % 900, channels[i % channels.length]),
    social: { linkedin: { url: `https://linkedin.com/in/seed${i}`, connected: i % 3 === 0 } },
  }));
  return [...CONTACTS, ...extra];
}

/* The 15 industry buckets, by key — the fixture only needs the keys. */
const INDUSTRY_KEYS = {
  film_tv: 1, commercial_product: 1, music: 1, fashion: 1, gaming: 1,
  spatial: 1, museum_archival: 1, visual_art: 1, gallery: 1,
  engineering_manufacturing: 1, architecture_real_estate: 1,
  education_research_institutional: 1, medical: 1, theatre: 1, other: 1,
};

/** Replace a record in a list by id, appending it when it is new — what the
 *  real DataProvider does after a Firestore write. */
const upsert = (list, record) => {
  const i = list.findIndex((r) => r.id === record.id);
  if (i < 0) return [...list, record];
  const next = [...list];
  next[i] = record;
  return next;
};

export default function E2ECrmFixture() {
  const [tab, setTab] = useState(0);
  const [contacts, setContacts] = useState(seededContacts);
  const [clients, setClients] = useState(CLIENTS);
  const [segments, setSegments] = useState(SEGMENTS);
  const [campaigns, setCampaigns] = useState(CAMPAIGNS);

  /* The stub writes "server-side" and reloadCampaigns reads back, so both need
     the live list without waiting for a render. */
  const campaignsRef = useRef(campaigns);
  campaignsRef.current = campaigns;
  server.write = (id, fn) => {
    const next = campaignsRef.current.map((c) => (c.id === id ? fn(c) : c));
    campaignsRef.current = next;
    setCampaigns(next);
  };
  const value = {
    contacts,
    clients,
    segments,
    campaigns,
    jobs: JOBS,
    savedQuotes: QUOTES,
    holds: HOLDS,
    aiConversations: [],
    // The palette gates the Messages group on this role, and so does the
    // Clients tab's open-item query, the same way the app does through
    // jobAccess(users/{uid}.role).
    role: "admin",
    /* The saves write to local state rather than doing nothing, because the
       drawers read the saved record back out of the collection — a no-op save
       looked like a save that silently lost the edit, which is exactly the
       failure the tags spec is there to catch. */
    saveContact: async (record) => { setContacts((list) => upsert(list, record)); },
    saveClient: async (record) => { setClients((list) => upsert(list, record)); },
    removeContact: async (id) => { setContacts((list) => list.filter((r) => r.id !== id)); },
    removeClient: async (id) => { setClients((list) => list.filter((r) => r.id !== id)); },
    /* `?slowsave=400` holds the write open for N ms, which is the only way to
       be inside an in-flight save from a spec: the guards around a completion
       that lands after the operator has moved on cannot be exercised against a
       save that resolves in a microtask. Off unless the parameter is there. */
    saveSegment: async (record) => {
      if (SLOW_SAVE_MS) await new Promise((res) => setTimeout(res, SLOW_SAVE_MS));
      const saved = { ...record, updatedAt: new Date().toISOString() };
      setSegments((list) => upsert(list, saved));
      return saved;
    },
    // Soft, the way DataContext does it — a campaign keeps something to point at.
    removeSegment: async (id) => {
      setSegments((list) => list.map((r) => (r.id === id ? { ...r, deletedAt: new Date().toISOString() } : r)));
    },
    saveCampaign: async (record) => { setCampaigns((list) => upsert(list, record)); },
    /* Reads the fixture's own state back, the way DataContext re-reads
       Firestore: a fresh list, and the caller finds its record in it. */
    reloadCampaigns: async () => campaignsRef.current,
  };
  return (
    <DataContextTestProvider value={value}>
      {/* Same width the app shell gives an app (App.jsx styles.appContent). */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: 24, fontFamily: font, background: C.bg, minHeight: "100vh" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }} className="crm-fixture-tabs">
          {["Contacts", "Clients", "Segments", "Campaigns"].map((label, i) => (
            <button key={label} style={{ ...btnS, fontWeight: tab === i ? 500 : 300 }} onClick={() => setTab(i)}>
              {label}
            </button>
          ))}
        </div>
        <CrmApp activeTab={tab} setActiveTab={setTab} user={{ email: "ivin@newyorkcapture.com" }} />
      </div>
    </DataContextTestProvider>
  );
}
